import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Barbell } from "@phosphor-icons/react";
import { OverviewCard, OverviewEmpty } from "./Card";

/**
 * Extra widget (g) — Volume settimanale per gruppo muscolare.
 *
 * Volume = Σ(kg × reps) negli ultimi 7 giorni, raggruppato per muscolo primario
 * dell'esercizio (lookup nella libreria).
 */

export function WeeklyVolumeByMuscleWidget({ logs, exerciseLibrary }) {
  const series = useMemo(() => {
    if (!logs?.length || !exerciseLibrary?.length) return [];
    // Build lookup from exercise name → muscle_group.
    const muscleByName = new Map();
    exerciseLibrary.forEach((ex) => {
      if (ex?.name) muscleByName.set(ex.name.trim().toLowerCase(), ex.muscle_group || null);
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const since7d = new Date(today);
    since7d.setDate(today.getDate() - 6); // last 7 days inclusive

    const volByMuscle = new Map();
    logs.forEach((l) => {
      if (!l.date) return;
      const d = new Date(l.date);
      d.setHours(0, 0, 0, 0);
      if (d < since7d || d > today) return;
      const muscle = muscleByName.get((l.exercise_name || "").trim().toLowerCase());
      if (!muscle) return;
      const vol = (l.sets || []).reduce((sum, s) => {
        const w = Number(s.weight_kg) || 0;
        const r = Number(s.reps) || 0;
        return sum + w * r;
      }, 0);
      volByMuscle.set(muscle, (volByMuscle.get(muscle) || 0) + vol);
    });

    return Array.from(volByMuscle.entries())
      .map(([muscle, value]) => ({ muscle, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value);
  }, [logs, exerciseLibrary]);

  const tooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#171717] border border-[#262626] rounded-lg p-2.5 text-xs shadow-2xl">
        <p className="text-zinc-400 mb-1">{label}</p>
        <p className="text-white font-medium">{payload[0].value} kg·rep</p>
      </div>
    );
  };

  return (
    <OverviewCard
      title="Volume 7 giorni"
      icon={Barbell}
      iconColor="text-[#AF52DE]"
      testId="overview-weekly-volume"
    >
      {series.length === 0 ? (
        <OverviewEmpty message="Nessun volume rilevato nell'ultima settimana." />
      ) : (
        <div className="h-[200px] w-full -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
              <XAxis dataKey="muscle" stroke="#52525B" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={50} />
              <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
              <Tooltip content={tooltip} cursor={{ fill: "#1A1A1A" }} />
              <Bar dataKey="value" fill="#AF52DE" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </OverviewCard>
  );
}
